import getDecorators from "inversify-inject-decorators";
import container from "../../../ioc/inversify.config";
import TYPES from "../../../ioc/types";
import { KeyboardMappingService } from "../../../services/keyboardmapping-service";
import { SceneId, SocketEvents } from "../../../shared/enum";
import { DefaultEventService } from "../../../services/event-service";
import { Controls } from "../../../shared/domain";
import {
    DialogMessageEvent,
    DialogStartEvent,
    DialogCompleteEvent,
    DialogErrorEvent,
    DialogChoiceRequest,
    DialogContinueRequest,
    DialogExitRequest,
} from "../../../shared/dtos";
import { DialogPanel } from "../../components/dialog-panel";

const { lazyInject } = getDecorators(container);

export interface DialogSceneParams {
    scene: Phaser.Scene;
}

export class DialogScene extends Phaser.Scene {
    private callingScene: Phaser.Scene;
    private dialogPanel: DialogPanel;
    private controls: Controls;

    // Dialog state
    private currentContextId: string | null = null;
    private currentMessage: DialogMessageEvent | null = null;
    private selectedChoiceIndex: number = 0;

    @lazyInject(TYPES.KeyboardMappingService)
    private keyboardMappingService: KeyboardMappingService;

    @lazyInject(TYPES.EventService)
    private eventService: DefaultEventService;

    constructor() {
        super({ key: SceneId.BASIC_DIALOG });
    }

    init(params: DialogSceneParams) {
        console.log("Initializing dialog scene");
        this.callingScene = params.scene;
        this.controls = this.keyboardMappingService.getKeyboardMapping("mikado");
    }

    create(params: DialogSceneParams) {
        console.log("Creating dialog scene");

        // Bind events
        this.bindSocketEvents();
        this.bindKeyboardEvents();

        // Listen for wake/sleep
        this.sys.events.on("wake", this.onWake, this).on("sleep", this.onSleep, this);
    }

    private bindSocketEvents() {
        console.log("Binding dialog socket events");

        this.eventService.register(
            SocketEvents.DIALOG_START,
            (data) => this.onDialogStart(data),
            this
        );

        this.eventService.register(
            SocketEvents.DIALOG_MESSAGE,
            (data) => this.onDialogMessage(data),
            this
        );

        this.eventService.register(
            SocketEvents.DIALOG_COMPLETE,
            (data) => this.onDialogComplete(data),
            this
        );

        this.eventService.register(
            SocketEvents.DIALOG_ERROR,
            (data) => this.onDialogError(data),
            this
        );
    }

    private unbindSocketEvents() {
        console.log("Unbinding dialog socket events");
        this.eventService.off(SocketEvents.DIALOG_START);
        this.eventService.off(SocketEvents.DIALOG_MESSAGE);
        this.eventService.off(SocketEvents.DIALOG_COMPLETE);
        this.eventService.off(SocketEvents.DIALOG_ERROR);
    }

    private bindKeyboardEvents() {
        this.input.keyboard.on("keydown-" + this.controls.actionKey, () => {
            this.onActionKey();
        });

        this.input.keyboard.on("keydown-" + this.controls.cancelKey, () => {
            this.onCancelKey();
        });

        this.input.keyboard.on("keydown-LEFT", () => {
            this.onLeftKey();
        });

        this.input.keyboard.on("keydown-RIGHT", () => {
            this.onRightKey();
        });
    }

    private unbindKeyboardEvents() {
        this.input.keyboard.off("keydown-" + this.controls.actionKey);
        this.input.keyboard.off("keydown-" + this.controls.cancelKey);
        this.input.keyboard.off("keydown-LEFT");
        this.input.keyboard.off("keydown-RIGHT");
    }

    // ========================================
    // Socket Event Handlers
    // ========================================

    private onDialogStart(data: DialogStartEvent) {
        console.log("===== DIALOG START =====");
        console.log("Full data received:", JSON.stringify(data, null, 2));
        console.log("context_id:", data.context_id);
        console.log("dialog_id:", data.dialog_id);
        console.log("content:", data.content);
        console.log("choices:", data.choices);
        console.log("can_exit:", data.can_exit);
        console.log("metadata:", data.metadata);
        console.log("=======================");

        this.currentContextId = data.context_id;
        this.currentMessage = data as DialogMessageEvent; // Now contains full dialog data
        this.selectedChoiceIndex = 0;

        // Wake scene and pause calling scene
        if (this.scene.isSleeping()) {
            this.scene.wake();
        }
        this.scene.bringToTop();
        this.callingScene.scene.pause();

        // Display the initial dialog message immediately
        this.displayMessage(data as DialogMessageEvent);
    }

    private onDialogMessage(data: DialogMessageEvent) {
        console.log("Dialog message received:", data);

        this.currentContextId = data.context_id;
        this.currentMessage = data;
        this.selectedChoiceIndex = 0;

        this.displayMessage(data);
    }

    private onDialogComplete(data: DialogCompleteEvent) {
        console.log("Dialog complete:", data);

        this.currentContextId = null;
        this.currentMessage = null;

        // Clean up and return to game
        this.closeDialog();
    }

    private onDialogError(data: DialogErrorEvent) {
        console.error("Dialog error:", data);

        // Show error briefly, then close
        // TODO: Better error display
        alert(`Dialog Error: ${data.error_message}`);

        this.closeDialog();
    }

    // ========================================
    // Display Logic
    // ========================================

    private displayMessage(message: DialogMessageEvent) {
        console.log("displayMessage called with:", message);
        console.log("Message content:", message.content);
        console.log("Message text:", message.content?.message);
        console.log("Speaker:", message.content?.speaker);
        console.log("Portrait online image:", message.portrait_online_image);
        console.log("Choices:", message.choices);

        // Clear previous dialog box
        if (this.dialogPanel) {
            this.dialogPanel.destroy();
        }

        // Create new dialog panel (portrait position can be configured here)
        // Default to 'left', but could be made configurable per dialog or globally
        const portraitPosition: "left" | "right" = "left";
        this.dialogPanel = new DialogPanel(this, portraitPosition);

        // Set speaker name if present
        if (message.content && message.content.speaker) {
            console.log("Setting speaker:", message.content.speaker.display_name);
            this.dialogPanel.setSpeaker(message.content.speaker.display_name);
        }

        // Set portrait if available (from character - this is the full URL)
        if (message.portrait_online_image) {
            console.log("Setting portrait:", message.portrait_online_image);
            this.dialogPanel.setPortrait(message.portrait_online_image);
        }

        // Set message text
        if (message.content && message.content.message) {
            console.log("Setting message text:", message.content.message);
            this.dialogPanel.setText(message.content.message);
        }

        // Display choices if present
        if (message.choices && message.choices.length > 0) {
            console.log(
                "Setting choices:",
                message.choices.map((c) => c.text)
            );
            this.dialogPanel.setChoices(message.choices.map((c) => c.text));
            this.dialogPanel.setSelectedChoice(this.selectedChoiceIndex);
        }

        console.log("Calling dialogPanel.draw()");
        this.dialogPanel.draw();
    }

    // ========================================
    // Keyboard Handlers
    // ========================================

    private onActionKey() {
        if (!this.currentContextId || !this.currentMessage) {
            return;
        }

        // If there are choices, select the current one
        if (this.currentMessage.choices && this.currentMessage.choices.length > 0) {
            const selectedChoice = this.currentMessage.choices[this.selectedChoiceIndex];

            // Note: save_id is managed by backend session (set during load_game_request)
            const request: DialogChoiceRequest = {
                context_id: this.currentContextId,
                choice_id: selectedChoice.choice_id,
            };

            console.log("Sending dialog choice:", request);
            this.eventService.emit(SocketEvents.DIALOG_CHOICE, request);
        } else {
            // No choices, just continue
            // Note: save_id is managed by backend session (set during load_game_request)
            const request: DialogContinueRequest = {
                context_id: this.currentContextId,
            };

            console.log("Sending dialog continue:", request);
            this.eventService.emit(SocketEvents.DIALOG_CONTINUE, request);
        }
    }

    private onCancelKey() {
        if (!this.currentContextId || !this.currentMessage) {
            return;
        }

        // Only allow exit if can_exit is true
        if (!this.currentMessage.can_exit) {
            console.log("Cannot exit dialog at this point");
            return;
        }

        // Exit dialog
        // Note: save_id is managed by backend session (set during load_game_request)
        const request: DialogExitRequest = {
            context_id: this.currentContextId,
        };

        console.log("Sending dialog exit:", request);
        this.eventService.emit(SocketEvents.DIALOG_EXIT, request);
    }

    private onLeftKey() {
        if (!this.currentMessage || !this.currentMessage.choices) {
            return;
        }

        // Move selection left
        this.selectedChoiceIndex = Math.max(0, this.selectedChoiceIndex - 1);
        this.dialogPanel.setSelectedChoice(this.selectedChoiceIndex);
    }

    private onRightKey() {
        if (!this.currentMessage || !this.currentMessage.choices) {
            return;
        }

        // Move selection right
        this.selectedChoiceIndex = Math.min(
            this.currentMessage.choices.length - 1,
            this.selectedChoiceIndex + 1
        );
        this.dialogPanel.setSelectedChoice(this.selectedChoiceIndex);
    }

    // ========================================
    // Lifecycle
    // ========================================

    private closeDialog() {
        // Destroy dialog box
        if (this.dialogPanel) {
            this.dialogPanel.destroy();
            this.dialogPanel = null;
        }

        // Resume calling scene
        this.callingScene.scene.resume();

        // Sleep this scene
        this.scene.sleep();
    }

    private onWake(scene, params) {
        console.log("Dialog scene waking");
        if (params && params.scene) {
            this.callingScene = params.scene;
        }
        this.bindKeyboardEvents();
    }

    private onSleep() {
        console.log("Dialog scene sleeping");
        this.unbindKeyboardEvents();
    }
}
